<template>
  <div>
    <div class="form-group">
      <div
        v-if="showAddressVisibilityLabel"
        class="col-md-6 col-md-offset-4 col-xs-12 text-left small m-b-10"
      >
        {{ $t('On your profile, we only show the name of your city.') }}
      </div>
      <label :for="address.fieldName" class="col-md-4 col-xs-4 control-label">
        {{ $t('Address') }}
      </label>
      <div class="col-md-6 col-xs-8">
        <input
          v-model="address.value"
          :required="address.required"
          :name="address.fieldName"
          :placeholder="$t('Address')"
          type="text"
          class="form-control"
          data-test="edit-location-address"
        />
        <span v-if="address.required" class="asterisk">*</span>
      </div>
    </div>

    <div v-if="showZipcodeField" class="form-group">
      <label :for="zipcode.fieldName" class="col-md-4 col-xs-4 control-label">
        <template v-if="selectedCountry !== '' && selectedCountry.toLowerCase() === 'us'">
          {{ $t('ZIP code') }}
        </template>
        <template v-else>
          {{ $t('Postcode') }}
        </template>
      </label>
      <div class="col-md-3 col-xs-8">
        <input
          v-model="zipcode.value"
          :required="zipcode.required"
          :name="zipcode.fieldName"
          :placeholder="$t('ZIP code')"
          type="text"
          class="form-control col-md-2"
          data-test="edit-location-zip"
        />
        <span v-if="zipcode.required" class="asterisk">*</span>
      </div>
    </div>

    <div class="form-group">
      <label :for="city.fieldName" class="col-md-4 col-xs-4 control-label">
        {{ $t('City') }}
      </label>
      <div class="col-md-6 col-xs-8">
        <input
          v-model="city.value"
          :required="city.required"
          :name="city.fieldName"
          :placeholder="$t('City')"
          type="text"
          class="form-control col-md-2"
          data-test="edit-location-city"
        />
        <span v-if="city.required" class="asterisk">*</span>
      </div>
    </div>

    <div class="form-group">
      <label :for="country.fieldName" class="col-md-4 col-xs-4 control-label">
        {{ $t('Country') }}
      </label>
      <div class="col-md-6 col-xs-8">
        <select
          v-model="selectedCountry"
          :required="country.required"
          :name="country.fieldName"
          :disabled="disableChangingCountry"
          :title="disableChangingCountry ? $t('Cannot change country with open offers') : ''"
          class="form-control"
          data-test="edit-location-country"
        >
          <option value="" data-test="edit-location-country-option-default">
            {{ $t('Select country') }}
          </option>
          <option
            v-for="(countryName, cca2) in countriesGlobal"
            :key="cca2"
            :value="cca2"
            :data-test="'edit-location-country-option-' + cca2"
          >
            {{ countryName }}
          </option>
        </select>
        <span v-if="country.required" class="asterisk">*</span>
      </div>
    </div>

    <div v-if="showCountryNotice" class="form-group p-l-15 p-r-15">
      <div class="bgm-lightgray p-15 text-center">
        <p>
          {{
            $t(
              'It appears you are signing up from a country that does not match our current active markets. Worksome specialises in matching local freelancers with local companies.',
            )
          }}
        </p>
        <p class="m-b-0">
          {{
            $t(
              'If you are located outside of our active markets, we will sadly not be able to approve your profile until we will launch in your country of residency.',
            )
          }}
        </p>
      </div>
    </div>

    <div
      v-if="selectedCountry !== '' && stateCountries.includes(selectedCountry.toUpperCase())"
      class="form-group"
    >
      <label :for="state.fieldName" class="col-md-4 control-label">
        {{ selectedCountry.toUpperCase() === 'CA' ? $t('Province') : $t('State') }}
      </label>
      <div class="col-md-6">
        <select
          :required="state.required"
          :name="state.fieldName"
          class="form-control"
          data-test="edit-location-state"
        >
          <option value="" data-test="edit-location-state-option-default">
            {{
              selectedCountry.toUpperCase() === 'CA' ? $t('Select Province') : $t('Select State')
            }}
          </option>
          <option
            v-for="(stateName, stateShort) in allStates[selectedCountry.toUpperCase()]"
            :key="stateShort"
            :value="stateShort"
            :selected="state.value && stateShort.toLowerCase() === state.value.toLowerCase()"
            :data-test="'edit-location-state-option-' + stateShort"
          >
            {{ stateName }}
          </option>
        </select>
        <span v-if="state.required" class="asterisk">*</span>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus, { BusEvent } from '@/utils/EventBus'

export default {
  name: 'EditLocation',
  props: {
    persistentAddress: {
      type: Object,
      required: true,
    },
    persistentZipcode: {
      type: Object,
      required: true,
    },
    persistentCity: {
      type: Object,
      required: true,
    },
    persistentCountry: {
      type: Object,
      required: true,
    },
    persistentState: {
      type: Object,
      required: true,
    },
    showAddressVisibilityLabel: {
      type: Boolean,
      default: false,
    },
    disableChangingCountry: {
      type: Boolean,
      default: false,
    },
    hasClients: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:country'],
  data() {
    return {
      selectedCountry: this.persistentCountry.value
        ? this.persistentCountry.value.toUpperCase()
        : '',
      address: {
        required: true,
        fieldName: 'address',
        value: '',
        ...this.persistentAddress,
      },
      zipcode: {
        required: true,
        fieldName: 'zipcode',
        value: '',
        ...this.persistentZipcode,
      },
      city: {
        required: true,
        fieldName: 'city',
        value: '',
        ...this.persistentCity,
      },
      country: {
        required: true,
        fieldName: 'country',
        value: '',
        ...this.persistentCountry,
      },
      state: {
        required: true,
        fieldName: 'state',
        value: '',
        ...this.persistentState,
      },
    }
  },
  computed: {
    countriesGlobal() {
      return countriesGlobalVar
    },
    allStates() {
      return globalStates
    },
    launchedCountries() {
      return currentMarketCountries
    },
    stateCountries() {
      return countriesWithStates
    },
    showCountryNotice() {
      if (this.hasClients || !this.selectedCountry) {
        return false
      }

      return !this.launchedCountries.includes(this.selectedCountry.toLowerCase())
    },
    showZipcodeField() {
      // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
      return this.selectedCountry?.toLowerCase() != 'ae'
    },
    fullAddress() {
      return {
        country: this.selectedCountry,
        state: this.state.value,
        address: this.address.value,
        zipcode: this.zipcode.value,
      }
    },
  },
  watch: {
    fullAddress: {
      handler(val) {
        EventBus.emit(BusEvent.SELECTED_PROFILE_LOCATION_CHANGED, val)
      },
      deep: true,
    },
    selectedCountry(value) {
      this.$emit('update:country', value)
    },
  },
}
</script>

<style scoped>
.asterisk {
  color: #0199d2;
  position: absolute;
  top: 10px;
  right: 0px;
}
</style>
